// ═══════════════════════════════════════════════════════════════════
// GraceStage.jsx — THE GRACE REVIEW ROOM   ·   ?stage=graces
// ═══════════════════════════════════════════════════════════════════
// Every King's Grace, both pictures, side by side, so a review is a
// screenshot instead of a memory. No game state, no Firebase.
//
//   ?stage=graces                       → all 14, flat, on a companion
//   &c=<companionId>                    → which companion wears them
//   &g=<graceId>                        → one Grace, in 3D, big
//   &size=56                            → the flat tile size (grid test)
//   &cam=az,el,dist                     → 3D framing by URL
// Routed in index.js beside ?stage=bodies / folk.
// ═══════════════════════════════════════════════════════════════════
import { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { GRACES, GRACE_ORDER } from "./kingsGrace";
import { GraceAura3D } from "./GraceAura";
import CompanionSprite from "./CompanionSprite";
import { IEBeing } from "./ieBeingModels";
import { beingOf, measureSolid } from "./fieldBeings3D";

const q = (k, d = null) => { try { return new URLSearchParams(window.location.search).get(k) ?? d; } catch (_) { return d; } };
const BODY_H = 1.2;

function FittedBody({ companionId, turn }) {
  const info = useMemo(() => beingOf(companionId), [companionId]);
  const outer = useRef(); const inner = useRef(); const fit = useRef(false);
  useFrame((_, dt) => {
    if (turn && outer.current) outer.current.rotation.y += dt * 0.4;
    if (fit.current || !inner.current) return;
    const mm = measureSolid(inner.current);
    if (mm && mm.h > 1e-3) {
      const s = BODY_H / mm.h;
      inner.current.scale.setScalar(s);
      inner.current.position.set(-mm.cx * s, -mm.minY * s, -mm.cz * s);
      fit.current = true;
    }
  });
  if (!info) return null;
  return <group ref={outer}><group ref={inner}><IEBeing being={info.lineage} tier={info.tier} /></group></group>;
}

function Tile({ gid, companionId, size }) {
  const g = GRACES[gid];
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: size + 24 }}>
      <div style={{ position: "relative", width: size, height: size, isolation: "isolate",
        background: "radial-gradient(circle at 50% 60%, #1c1530, #07040f 70%)", borderRadius: 8 }}>
        <CompanionSprite companionId={companionId} size={size} radiantTier={gid} />
      </div>
      <div style={{ fontSize: 10, color: g.color, fontWeight: 900, textAlign: "center" }}>{g.label}</div>
      <div style={{ fontSize: 8, color: "#9a8fb0", textAlign: "center" }}>{g.crown} · {g.realm}</div>
    </div>
  );
}

export default function GraceStage() {
  const companionId = q("c", "spectral_tiger_lord");
  const one = q("g");
  const size = Number(q("size", 120)) || 120;
  const cam = (q("cam") || "").split(",").map(Number);
  const az = isFinite(cam[0]) ? cam[0] : 0.4, el = isFinite(cam[1]) ? cam[1] : 0.25, dist = isFinite(cam[2]) ? cam[2] : one ? 4 : 15;
  const list = one ? [one] : GRACE_ORDER;
  const cols = one ? 1 : 7;
  const pos = (i) => one ? [0, 0, 0] : [((i % cols) - (cols - 1) / 2) * 2.1, 0, (Math.floor(i / cols) - 0.5) * 2.6];
  return (
    <div style={{ position: "fixed", inset: 0, overflow: "auto", background: "#07040f", color: "#e8dcc8", fontFamily: "Georgia, serif", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "8px 12px", fontSize: 12, letterSpacing: 2 }}>
        ✦ THE GRACE REVIEW ROOM — {companionId} {one ? `· ${one}` : `· ${list.length} graces`}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: "0 12px 10px" }}>
        {list.map((gid) => <Tile key={gid} gid={gid} companionId={companionId} size={size} />)}
      </div>
      <div style={{ position: "relative", height: one ? "78vh" : "62vh", flexShrink: 0 }}>
        <Canvas dpr={[1, 1.5]} camera={{ fov: 32, position: [Math.sin(az) * Math.cos(el) * dist, 1 + Math.sin(el) * dist, Math.cos(az) * Math.cos(el) * dist] }}
          onCreated={(s) => { if (typeof window !== "undefined") window.__graceStage = s; }}>
          <color attach="background" args={["#07040f"]} />
          <hemisphereLight args={["#b8a8d8", "#4a3420", 0.7]} />
          <directionalLight position={[3, 5, 4]} intensity={1.3} color="#ffe2b0" />
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.002, 0]}><planeGeometry args={[40, 20]} /><meshStandardMaterial color="#171022" /></mesh>
          {list.map((gid, i) => (
            <group key={gid} position={pos(i)}>
              <FittedBody companionId={companionId} turn={q("turn") === "1"} />
              <GraceAura3D graceId={gid} height={BODY_H} />
            </group>
          ))}
          <OrbitControls target={[0, 0.6, 0]} makeDefault />
        </Canvas>
      </div>
    </div>
  );
}
